import { computed, ref } from 'vue'
import useVuexModule from '@shared/composables/vuexModule.js'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { useStyleProfileStore } from '@/stores/styleProfile.js'
import { usePlans } from '@shared/composables/plans.js'
import api from '@/store/api/api'
import { useLogger } from 'vue-logger-plugin'

// TODO: Partial implementation (ENG-2859)
export const useSubscriptionStore = defineStore('subscription', () => {
  const log = useLogger()

  // Fallback ref for membershipPlans when Vuex module isn't registered
  const membershipPlans = ref(null)

  // Fallback implementation for fetchPlans when Vuex module isn't registered
  const fetchPlans = async ({ forHomepage = false } = {}) => {
    try {
      const res = await api.apiSubscribe.getPlans(null, forHomepage)
      if (res.data.length) {
        membershipPlans.value = res.data
      }
    } catch (err) {
      log.error('Failed to fetch plans', err)
      throw err
    }
  }

  const { doNothing, ...vuex } = useVuexModule('subscribe', {
    defaults: {
      state: [
        ['membershipPlans', membershipPlans],
        'membershipPlanSelectedId'
      ],
      getters: [
        'membershipPlanSelected',
        ['membershipPlanFromId', () => doNothing]
      ],
      actions: [
        ['fetchPlans', fetchPlans],
        'selectMembershipPlan'
      ]
    }
  })

  const {
    membershipPlanSelectedId: selectedPlanId,
    membershipPlanSelected: selectedPlan,
    membershipPlanFromId,
  } = vuex

  const { planChoiceFrequency, planChoiceSpend } = storeToRefs(useStyleProfileStore())

  const recommendedPlanId = computed(() => {
    const { fourItemPlan, sevenItemPlan, unlimitedPlan } = usePlans()
    const fourItemPlanId = fourItemPlan.value?.id
    const sevenItemPlanId = sevenItemPlan.value?.id
    const unlimitedPlanId = unlimitedPlan.value?.id
    const map = {
      few_per_month: {
        '100_or_less': fourItemPlanId,
        '100_to_200': sevenItemPlanId,
        '200_or_more': unlimitedPlanId
      },
      once_per_week: {
        '100_or_less': sevenItemPlanId,
        '100_to_200': sevenItemPlanId,
        '200_or_more': unlimitedPlanId
      },
      several_per_week: {
        '100_or_less': sevenItemPlanId,
        '100_to_200': unlimitedPlanId,
        '200_or_more': unlimitedPlanId
      }
    }
    const onlyPlanAvailable = vuex.membershipPlans.value?.length === 1 ? vuex.membershipPlans.value[0].id : null
    return map[planChoiceFrequency.value]?.[planChoiceSpend.value] ?? unlimitedPlanId ?? onlyPlanAvailable
  })
  const recommendedPlan = computed(() => membershipPlanFromId.value(recommendedPlanId.value))
  const isRejoiningMember = ref(false)

  return {
    ...vuex,
    selectedPlanId,
    selectedPlan,
    recommendedPlanId,
    recommendedPlan,
    isRejoiningMember
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useSubscriptionStore, import.meta.hot))
